// src/pages/CrearUtensilio.js
import React, { useEffect, useMemo, useState } from "react";
import { db, authReady } from "../firebase";
import {
  addDoc,
  collection,
  getDocs,
  serverTimestamp,
  updateDoc,
  increment,
  doc,
  runTransaction,
} from "firebase/firestore";

// ---- Almacenes: nombre visible -> clave de ruta ----
const ALMACEN_OPCIONES = ["Estadio", "Torrecardenas", "Rambla"];
const ALMACEN_KEYS = {
  Estadio: "estadio",
  Torrecardenas: "torrecardenas",
  Rambla: "rambla",
};
const keyFromName = (visible) =>
  ALMACEN_KEYS[visible] || String(visible || "").toLowerCase();
const nameFromKey = (key) => {
  const k = String(key || "").toLowerCase();
  if (k === "estadio") return "Estadio";
  if (k === "torrecardenas") return "Torrecardenas";
  if (k === "rambla") return "Rambla";
  return null;
};

// ---- Normalizador: minúsculas + sin tildes/acentos ----
const normalizar = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default function CrearUtensilio() {
  const initialForm = useMemo(
    () => ({
      nombre: "",
      cantidad: 1,
      ubicacion: "Estadio",
      notas: "",
    }),
    []
  );

  const [form, setForm] = useState(initialForm);
  const [guardando, setGuardando] = useState(false);

  // --- Modal de duplicado ---
  const [dupOpen, setDupOpen] = useState(false);
  const [dupItem, setDupItem] = useState(null); // { id, nombre, cantidad, ubicacion, refPath }
  const [dupCantidad, setDupCantidad] = useState(1);

  // --- NUEVO: bloquear ubicación si viene de sesión (Estadio/Torrecardenas/Rambla) ---
  const [sesionAlmacenVisible, setSesionAlmacenVisible] = useState(null);
  useEffect(() => {
    const almacenKey = sessionStorage.getItem("almacenKey"); // "estadio" | "torrecardenas" | "rambla"
    const visible = nameFromKey(almacenKey);
    if (visible) {
      setSesionAlmacenVisible(visible);
      setForm((f) => ({ ...f, ubicacion: visible }));
    }
  }, []);

  function setF(campo, valor) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  // --- Guardar nuevo (o abrir modal si existe duplicado en misma ubicación) ---
  async function guardar() {
    if (!form.nombre.trim()) {
      alert("El nombre es obligatorio.");
      return;
    }
    const cantidad = Math.max(0, Math.trunc(Number(form.cantidad) || 0));
    const nombreNormalizado = normalizar(form.nombre);

    setGuardando(true);
    try {
      if (typeof authReady?.then === "function") {
        try {
          await authReady;
        } catch {}
      }

      // Si hay sesión de almacén, forzar esa ubicación
      const ubicacionVisible = sesionAlmacenVisible || form.ubicacion;
      const key = keyFromName(ubicacionVisible); // "estadio" | "torrecardenas" | "rambla"
      const colRef = collection(db, "almacenes", key, "items");

      // Traemos los docs del almacén y comparamos en cliente con normalización
      const snap = await getDocs(colRef);
      const duplicado = snap.docs.find(
        (d) => normalizar(d.data()?.nombre) === nombreNormalizado
      );

      if (duplicado) {
        // Ya existe: abre modal para modificar el existente
        const d = duplicado;
        setDupItem({
          id: d.id,
          nombre: d.data()?.nombre || form.nombre.trim(),
          cantidad: d.data()?.cantidad ?? 0,
          ubicacion: key,
          refPath: d.ref.path, // "almacenes/{key}/items/{id}"
        });
        setDupCantidad(Math.max(1, cantidad || 1)); // cantidad inicial para aplicar
        setDupOpen(true);
        setGuardando(false);
        return; // NO crear nuevo
      }

      // No existe: crear nuevo
      const payload = {
        nombre: form.nombre.trim(),
        cantidad,
        ubicacion: key,
        notas: (form.notas || "").trim(),
        creadoEn: serverTimestamp(),
      };

      await addDoc(colRef, payload);
      setForm((f) => ({
        ...initialForm,
        // si hay sesión, mantener la ubicación fija en el reset
        ubicacion: sesionAlmacenVisible || initialForm.ubicacion,
      }));
      alert("Utensilio guardado correctamente.");
    } catch (e) {
      console.error("Error al guardar:", e);
      alert("No se pudo guardar. Revisa la consola para más detalles.");
    } finally {
      setGuardando(false);
    }
  }

  // --- Helpers para modal ---
  function refFromPath(p) {
    // p.ej. "almacenes/estadio/items/abc123"
    const [c1, almacen, c2, id] = String(p || "").split("/");
    return doc(db, c1, almacen, c2, id);
  }

  async function dupSumar() {
    if (!dupItem) return;
    const n = Math.max(1, Math.trunc(Number(dupCantidad) || 0));
    try {
      if (typeof authReady?.then === "function") {
        try {
          await authReady;
        } catch {}
      }
      await updateDoc(refFromPath(dupItem.refPath), { cantidad: increment(n) });
      cerrarModalOK("Cantidad sumada correctamente.");
    } catch (e) {
      console.error("dupSumar error:", e);
      alert("No se pudo sumar.");
    }
  }

  async function dupRestar() {
    if (!dupItem) return;
    const n = Math.max(1, Math.trunc(Number(dupCantidad) || 0));
    try {
      if (typeof authReady?.then === "function") {
        try {
          await authReady;
        } catch {}
      }
      // Evitar valores negativos con transacción
      await runTransaction(db, async (tx) => {
        const ref = refFromPath(dupItem.refPath);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("No existe");
        const cur = Number(snap.data()?.cantidad || 0);
        const nuevo = Math.max(0, cur - n);
        tx.update(ref, { cantidad: nuevo });
      });
      cerrarModalOK("Cantidad restada correctamente.");
    } catch (e) {
      console.error("dupRestar error:", e);
      alert("No se pudo restar.");
    }
  }

  function cerrarModalOK(msg) {
    setDupOpen(false);
    setDupItem(null);
    setForm((f) => ({
      ...initialForm,
      // mantener ubicación según sesión
      ubicacion: sesionAlmacenVisible || initialForm.ubicacion,
    }));
    if (msg) alert(msg);
  }

  function cerrarModalCancel() {
    setDupOpen(false);
    // Dejamos el formulario tal cual por si quieres cambiar nombre/cantidad/notas
  }

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 text-slate-800">
        Crear utensilio
      </h1>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="flex flex-col">
          <label className="text-slate-600 text-sm mb-1 font-medium">
            Nombre del utensilio *
          </label>
          <input
            className="border rounded px-3 py-2"
            placeholder="Ej. Cuchillo, Bandeja, etc."
            value={form.nombre}
            onChange={(e) => setF("nombre", e.target.value)}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-slate-600 text-sm mb-1 font-medium">
            Cantidad
          </label>
          <input
            className="border rounded px-3 py-2"
            type="number"
            min={0}
            placeholder="Ej. 5"
            value={form.cantidad}
            onChange={(e) => setF("cantidad", e.target.value)}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-slate-600 text-sm mb-1 font-medium">
            Ubicación
          </label>
          <select
            className="border rounded px-3 py-2"
            value={form.ubicacion}
            onChange={(e) => setF("ubicacion", e.target.value)}
            disabled={!!sesionAlmacenVisible} // <-- bloquea si hay sesión
          >
            {(sesionAlmacenVisible ? [sesionAlmacenVisible] : ALMACEN_OPCIONES).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          {sesionAlmacenVisible && (
            <p className="text-xs text-slate-500 mt-1">
              * Fijado por la sesión: solo puedes crear en “{sesionAlmacenVisible}”.
            </p>
          )}
        </div>

        <div className="flex flex-col md:col-span-2">
          <label className="text-slate-600 text-sm mb-1 font-medium">
            Notas (opcional)
          </label>
          <textarea
            className="border rounded px-3 py-2"
            placeholder="Información adicional..."
            value={form.notas}
            onChange={(e) => setF("notas", e.target.value)}
            rows={4}
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={guardar}
          disabled={guardando}
          className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700 transition"
        >
          {guardando ? "Guardando..." : "Guardar"}
        </button>
      </div>

      {/* ------- Modal de duplicado ------- */}
      {dupOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5">
            <h2 className="text-xl font-semibold mb-2">
              Ya existe en “{form.ubicacion}”
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              El utensilio <strong>{dupItem?.nombre}</strong> ya está creado en este almacén.
              Actual: <strong>{dupItem?.cantidad ?? 0}</strong> unidades.
            </p>

            <label className="text-sm block mb-3">
              Cantidad a aplicar
              <input
                type="number"
                min={1}
                className="border rounded px-3 py-2 w-full mt-1"
                value={dupCantidad}
                onChange={(e) =>
                  setDupCantidad(Math.max(1, Math.trunc(Number(e.target.value) || 0)))
                }
              />
            </label>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={dupSumar}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
              >
                Sumar
              </button>
              <button
                onClick={dupRestar}
                className="px-4 py-2 rounded bg-yellow-600 text-white hover:bg-yellow-700"
              >
                Restar
              </button>
              <button
                onClick={cerrarModalCancel}
                className="px-4 py-2 rounded border"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
